import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStudentDashboard, CourseDashboardDTO } from '../services/studentDashboardService';
import {
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Box,
    Chip,
    LinearProgress,
    Alert,
    CircularProgress,
    Paper,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    IconButton,
} from '@mui/material';
import {
    PlayArrow,
    CheckCircle,
    Timer,
    ArrowForward,
    MenuBook,
    Assignment,
    Quiz,
    EmojiEvents,
    School,
    Warning,
    TrendingUp,
    Close,
    Star,
    FlashOn,
    Leaderboard,
} from '@mui/icons-material';

interface User {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    userType: string;
}

interface Course {
    id: number;
    name: string;
    description: string;
    currentTopic: {
        id: number;
        title: string;
        subject: string;
        subjectId: number;
        progress: number;
        estimatedTime: string;
        status: 'in_progress' | 'not_started' | 'completed';
    };
    classProgress: {
        currentTopicTitle: string;
        studentsBehind: number;
        totalStudents: number;
        weeksBehind: number;
    };
    pendingItems: {
        assignments: number;
        quizzes: number;
        overdueAssignments: number;
    };
    recentAchievements: {
        id: number;
        title: string;
        type: 'quiz' | 'assignment' | 'topic' | 'award';
        score?: number;
        earnedDate: string;
    }[];
    completedTopics: {
        id: number;
        title: string;
        subject: string;
        subjectId: number;
        completedAt: string;
        quizScore?: number;
        estimatedTime: string;
        quickQuiz: {
            question: string;
            options: string[];
            correctAnswer: number;
            explanation: string;
        };
        additionalQuestions?: {
            question: string;
            options: string[];
            correctAnswer: number;
            explanation: string;
        }[];
        requizCompleted?: boolean;
    }[];
    requizMasterPoints: number;
    classRanking: {
        position: number;
        totalStudents: number;
        points: number;
    };
    courseType?: string;
}


const StudentCourses: React.FC = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState<User | null>(null);
    const [courses, setCourses] = useState<Course[]>([]);
    const [loading, setLoading] = useState(true);
    const [quizModal, setQuizModal] = useState<{
        open: boolean;
        courseId: number;
        topic: Course['completedTopics'][0] | null;
        selectedAnswer: number | null;
        showResult: boolean;
        isCorrect: boolean;
        currentQuestionIndex: number;
        totalQuestions: number;
        currentQuestion: {
            question: string;
            options: string[];
            correctAnswer: number;
            explanation: string;
        } | null;
    }>({
        open: false,
        courseId: 0,
        topic: null,
        selectedAnswer: null,
        showResult: false,
        isCorrect: false,
        currentQuestionIndex: 0,
        totalQuestions: 1,
        currentQuestion: null,
    });

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setLoading(true);

                // Get user data from localStorage
                const userData = localStorage.getItem('user');
                if (userData) {
                    setUser(JSON.parse(userData));
                }

                // Fetch dashboard data from API
                const dashboardData = await getStudentDashboard();

                // Map API response to component Course interface
                const mappedCourses = dashboardData.courses.map(mapCourseDashboardToCourse);

                setCourses(mappedCourses);
            } catch (error) {
                console.error('Error fetching dashboard:', error);
                // Fallback to empty courses on error
                setCourses([]);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    // Helper function to map CourseDashboardDTO to Course interface
    const mapCourseDashboardToCourse = (courseDTO: CourseDashboardDTO): Course => {
        // Map completed topics
        const completedTopics = courseDTO.completedTopics.map(topic => ({
            id: topic.topicId,
            title: topic.title,
            subject: topic.subjectName,
            subjectId: topic.subjectId,
            completedAt: topic.completedAt,
            quizScore: topic.quizScore,
            estimatedTime: topic.estimatedTime,
            quickQuiz: {
                question: '', // TODO: Get from quiz API
                options: [],
                correctAnswer: 0,
                explanation: ''
            },
            additionalQuestions: [],
            requizCompleted: topic.requizCompleted || false
        }));

        // Map current topic
        const currentTopic = courseDTO.currentTopic ? {
            id: courseDTO.currentTopic.topicId,
            title: courseDTO.currentTopic.title,
            subject: courseDTO.currentTopic.subjectName,
            subjectId: courseDTO.currentTopic.subjectId,
            progress: courseDTO.currentTopic.progress,
            estimatedTime: courseDTO.currentTopic.estimatedTime,
            status: courseDTO.currentTopic.status as 'in_progress' | 'not_started' | 'completed'
        } : null;

        // Map class progress
        const classProgress = courseDTO.classProgress ? {
            currentTopicTitle: courseDTO.classProgress.currentTopicTitle,
            studentsBehind: courseDTO.classProgress.studentsBehind,
            totalStudents: courseDTO.classProgress.totalStudents,
            weeksBehind: courseDTO.classProgress.weeksBehind
        } : null;

        // Map ranking
        const classRanking = courseDTO.requizMasterRanking ? {
            position: courseDTO.requizMasterRanking.position,
            totalStudents: courseDTO.requizMasterRanking.totalStudents,
            points: courseDTO.requizMasterRanking.totalPoints
        } : null;

        return {
            id: courseDTO.courseId,
            name: courseDTO.courseName || 'Course',
            description: courseDTO.courseDescription || '',
            courseType: courseDTO.courseType,
            currentTopic: currentTopic || {
                id: 0,
                title: 'No topic available',
                subject: '',
                subjectId: 0,
                progress: 0,
                estimatedTime: '',
                status: 'not_started'
            },
            classProgress: classProgress,
            pendingItems: {
                assignments: courseDTO.pendingItems?.assignments || 0,
                quizzes: courseDTO.pendingItems?.quizzes || 0,
                overdueAssignments: courseDTO.pendingItems?.overdueAssignments || 0
            },
            recentAchievements: courseDTO.recentAchievements.map(achievement => ({
                id: achievement.id,
                title: achievement.title,
                type: achievement.type as 'quiz' | 'assignment' | 'topic' | 'award',
                score: achievement.score,
                earnedDate: achievement.earnedDate
            })),
            completedTopics: completedTopics,
            requizMasterPoints: courseDTO.requizMasterPoints || 0,
            classRanking: classRanking
        };
    };

    const handleContinueTopic = (course: Course) => {
        const topic = course.currentTopic;
        if (course.courseType === 'ABACUS_MATHS') {
            navigate(`/student-courses/${course.id}/abacus/topics/${topic.id}`);
            return;
        }
        navigate(`/student-courses/${course.id}/subjects/${topic.subjectId}/topics/${topic.id}`);
    };

    const handleViewCourse = (courseId: number, courseType?: string) => {
        if (courseType === 'ABACUS_MATHS') {
            navigate(`/student-courses/${courseId}/abacus`);
            return;
        }
        navigate(`/student-courses/${courseId}`);
    };

    const handleQuickQuiz = (courseId: number, topic: Course['completedTopics'][0]) => {
        if (topic.requizCompleted) return; // Already completed

        const allQuestions = [topic.quickQuiz, ...(topic.additionalQuestions || [])];

        setQuizModal({
            open: true,
            courseId,
            topic,
            selectedAnswer: null,
            showResult: false,
            isCorrect: false,
            currentQuestionIndex: 0,
            totalQuestions: allQuestions.length,
            currentQuestion: allQuestions[0],
        });
    };

    const handleQuizAnswerChange = (value: string) => {
        setQuizModal(prev => ({
            ...prev,
            selectedAnswer: parseInt(value)
        }));
    };

    const handleQuizSubmit = () => {
        if (quizModal.selectedAnswer === null || !quizModal.currentQuestion) return;

        const isCorrect = quizModal.selectedAnswer === quizModal.currentQuestion.correctAnswer;

        setQuizModal(prev => ({
            ...prev,
            showResult: true,
            isCorrect
        }));

        if (isCorrect && quizModal.currentQuestionIndex === 0) {
            // Only give points for the first question (original quick quiz)
            setCourses(prevCourses =>
                prevCourses.map(course =>
                    course.id === quizModal.courseId
                        ? {
                            ...course,
                            requizMasterPoints: course.requizMasterPoints + 1,
                            completedTopics: course.completedTopics.map(t =>
                                t.id === quizModal.topic?.id
                                    ? { ...t, requizCompleted: true }
                                    : t
                            )
                        }
                        : course
                )
            );
        }
    };

    const handleNextQuestion = () => {
        if (!quizModal.topic) return;

        const allQuestions = [quizModal.topic.quickQuiz, ...(quizModal.topic.additionalQuestions || [])];
        const nextIndex = quizModal.currentQuestionIndex + 1;

        if (nextIndex < allQuestions.length) {
            setQuizModal(prev => ({
                ...prev,
                currentQuestionIndex: nextIndex,
                currentQuestion: allQuestions[nextIndex],
                selectedAnswer: null,
                showResult: false,
                isCorrect: false,
            }));
        } else {
            handleCloseQuiz();
        }
    };

    const handleCloseQuiz = () => {
        setQuizModal({
            open: false,
            courseId: 0,
            topic: null,
            selectedAnswer: null,
            showResult: false,
            isCorrect: false,
            currentQuestionIndex: 0,
            totalQuestions: 1,
            currentQuestion: null,
        });
    };

    const handleViewAssignments = (courseId: number) => {
        navigate(`/student-courses/${courseId}/assignments`);
    };

    const handleViewQuizzes = (courseId: number) => {
        navigate(`/student-courses/${courseId}/quiz`);
    };

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (!user) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Alert severity="error">Please log in to access your dashboard.</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
            {/* My Courses - Hero Section */}
            <Box sx={{ mb: 5 }}>
                <Typography variant="h5" fontWeight="600" sx={{ mb: 3, color: 'text.primary' }}>
                    My Courses
                </Typography>

                {courses.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                        <School sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No courses enrolled
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Get started by registering for a course
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => navigate('/register-course')}
                            sx={{
                                borderRadius: 3,
                                px: 4,
                                py: 1.5,
                                fontWeight: 600,
                            }}
                        >
                            Register for Course
                        </Button>
                    </Paper>
                ) : (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 3
                    }}>
                        {courses.map((course) => (
                            <Card
                                key={course.id}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    width: '100%',
                                    maxWidth: courses.length === 1 ? '800px' : '100%',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}
                            >
                                <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    {/* Course Header */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <MenuBook color="primary" />
                                                <Typography variant="h6" fontWeight="600">
                                                    {course.name}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" color="text.secondary">
                                                {course.description}
                                            </Typography>
                                        </Box>
                                        <Button
                                            variant="outlined"
                                            onClick={() => handleViewCourse(course.id, course.courseType)}
                                            sx={{ minWidth: 120 }}
                                        >
                                            View Course
                                        </Button>
                                    </Box>

                                    <Grid container spacing={3} sx={{ flex: 1 }}>
                                        {/* LEFT SIDE - ACTIONS */}
                                        <Grid item xs={12} md={6}>
                                            {/* Current Topic */}
                                            <Box sx={{ mb: 3 }}>
                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                    🎯 CONTINUE LEARNING
                                                </Typography>
                                                <Card
                                                    sx={{
                                                        cursor: 'pointer',
                                                        bgcolor: 'primary.50',
                                                        '&:hover': {
                                                            boxShadow: 2,
                                                            transform: 'translateY(-1px)',
                                                            transition: 'all 0.2s ease-in-out'
                                                        }
                                                    }}
                                                    onClick={() => handleContinueTopic(course)}
                                                >
                                                    <CardContent sx={{ p: 2 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                            <PlayArrow color="primary" />
                                                            <Typography variant="subtitle1" fontWeight="500">
                                                                {course.currentTopic.title}
                                                            </Typography>
                                                        </Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                            {course.currentTopic.subject}
                                                        </Typography>

                                                        <Box sx={{ mb: 2 }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Progress: {course.currentTopic.progress}%
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <Timer sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {course.currentTopic.estimatedTime}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={course.currentTopic.progress}
                                                                sx={{ height: 6, borderRadius: 3 }}
                                                            />
                                                        </Box>

                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            endIcon={<ArrowForward />}
                                                            fullWidth
                                                        >
                                                            Continue Learning
                                                        </Button>
                                                    </CardContent>
                                                </Card>
                                            </Box>

                                            {/* Completed Topics - Quick Quiz */}
                                            {course.completedTopics.length > 0 && (
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                        ⚡ QUICK QUIZ - EARN POINTS
                                                    </Typography>
                                                    <Grid container spacing={1}>
                                                        {course.completedTopics.slice(0, 3).map((topic) => (
                                                            <Grid item xs={12} key={topic.id}>
                                                                <Card
                                                                    sx={{
                                                                        cursor: topic.requizCompleted ? 'default' : 'pointer',
                                                                        bgcolor: topic.requizCompleted ? 'success.50' : 'warning.50',
                                                                        opacity: topic.requizCompleted ? 0.7 : 1,
                                                                        '&:hover': topic.requizCompleted ? {} : {
                                                                            boxShadow: 1,
                                                                            transform: 'translateY(-1px)',
                                                                            transition: 'all 0.2s ease-in-out'
                                                                        }
                                                                    }}
                                                                    onClick={() => handleQuickQuiz(course.id, topic)}
                                                                >
                                                                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                {topic.requizCompleted ? (
                                                                                    <CheckCircle color="success" sx={{ fontSize: 16 }} />
                                                                                ) : (
                                                                                    <FlashOn color="warning" sx={{ fontSize: 16 }} />
                                                                                )}
                                                                                <Box>
                                                                                    <Typography variant="body2" fontWeight="500">
                                                                                        {topic.title}
                                                                                    </Typography>
                                                                                    <Typography variant="caption" color="text.secondary">
                                                                                        {topic.subject}
                                                                                    </Typography>
                                                                                </Box>
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                {topic.requizCompleted ? (
                                                                                    <Chip
                                                                                        label="✓ Done"
                                                                                        size="small"
                                                                                        color="success"
                                                                                        icon={<Star />}
                                                                                    />
                                                                                ) : (
                                                                                    <Chip
                                                                                        label="+1 Point"
                                                                                        size="small"
                                                                                        color="warning"
                                                                                        icon={<FlashOn />}
                                                                                    />
                                                                                )}
                                                                            </Box>
                                                                        </Box>
                                                                    </CardContent>
                                                                </Card>
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </Box>
                                            )}
                                        </Grid>

                                        {/* RIGHT SIDE - INFORMATION (Two Columns) */}
                                        <Grid item xs={12} md={6}>
                                            <Grid container spacing={2}>
                                                {/* MIDDLE COLUMN - Class Progress & Pending Tasks */}
                                                <Grid item xs={12} md={6}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                        {/* Class Progress - Compact */}
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                                                                📚 CLASS PROGRESS
                                                            </Typography>
                                                            <Paper sx={{ p: 1.5, bgcolor: (course.classProgress?.weeksBehind ?? 0) > 0 ? 'warning.50' : 'success.50' }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                                    {(course.classProgress?.weeksBehind ?? 0) > 0 ? (
                                                                        <Warning sx={{ fontSize: 18 }} color="warning" />
                                                                    ) : (
                                                                        <TrendingUp sx={{ fontSize: 18 }} color="success" />
                                                                    )}
                                                                    <Box>
                                                                        <Typography variant="body2" fontWeight="500">
                                                                            {course.classProgress?.currentTopicTitle}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                                <Typography variant="caption" color={(course.classProgress?.weeksBehind ?? 0) > 0 ? "warning.dark" : "success.dark"}>
                                                                    {(course.classProgress?.weeksBehind ?? 0) > 0 ?
                                                                        `${course.classProgress!.weeksBehind} week${course.classProgress!.weeksBehind > 1 ? 's' : ''} behind` :
                                                                        "You're caught up!"
                                                                    }
                                                                </Typography>
                                                            </Paper>
                                                        </Box>

                                                        {/* Pending Tasks - Compact */}
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                                                                📋 PENDING TASKS
                                                            </Typography>
                                                            <Paper sx={{ bgcolor: 'grey.50' }}>
                                                                <Box sx={{ p: 1.5 }}>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <Assignment sx={{ fontSize: 16 }} color={course.pendingItems.overdueAssignments > 0 ? "error" : "primary"} />
                                                                            <Typography variant="body2">
                                                                                {course.pendingItems.assignments} Assignments
                                                                            </Typography>
                                                                        </Box>
                                                                        <Button size="small" variant="text" onClick={() => handleViewAssignments(course.id)}>
                                                                            View
                                                                        </Button>
                                                                    </Box>
                                                                    {course.pendingItems.overdueAssignments > 0 && (
                                                                        <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 1 }}>
                                                                            {course.pendingItems.overdueAssignments} overdue
                                                                        </Typography>
                                                                    )}
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <Quiz sx={{ fontSize: 16 }} color="info" />
                                                                            <Typography variant="body2">
                                                                                {course.pendingItems.quizzes} Quizzes
                                                                            </Typography>
                                                                        </Box>
                                                                        <Button size="small" variant="text" onClick={() => handleViewQuizzes(course.id)}>
                                                                            View
                                                                        </Button>
                                                                    </Box>
                                                                </Box>
                                                            </Paper>
                                                        </Box>
                                                    </Box>
                                                </Grid>

                                                {/* RIGHTMOST COLUMN - ReQuiz Ranking & Recent Achievements */}
                                                <Grid item xs={12} md={6}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                        {/* ReQuiz Master Points & Ranking - Compact */}
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                                                                🏆 REQUIZ MASTER RANKING
                                                            </Typography>
                                                            <Paper sx={{ p: 1.5, bgcolor: 'warning.50' }}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <Leaderboard sx={{ fontSize: 18 }} color="warning" />
                                                                        <Typography variant="subtitle2" fontWeight="bold" color="warning.dark">
                                                                            #{course.classRanking?.position}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <Star sx={{ fontSize: 18 }} color="warning" />
                                                                        <Typography variant="subtitle2" fontWeight="bold" color="warning.dark">
                                                                            {course.requizMasterPoints} pts
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                                <Typography variant="caption" color="warning.dark">
                                                                    Rank {course.classRanking?.position} of {course.classRanking?.totalStudents} students
                                                                </Typography>
                                                            </Paper>
                                                        </Box>

                                                        {/* Recent Achievements - Compact */}
                                                        {course.recentAchievements.length > 0 && (
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                                                                    🎉 RECENT ACHIEVEMENTS
                                                                </Typography>
                                                                <Paper sx={{ p: 1.5, bgcolor: 'success.50' }}>
                                                                    {course.recentAchievements.slice(0, 2).map((achievement, index) => (
                                                                        <Box key={achievement.id}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                                                {achievement.type === 'quiz' && <Quiz sx={{ fontSize: 16 }} color="success" />}
                                                                                {achievement.type === 'assignment' && <Assignment sx={{ fontSize: 16 }} color="success" />}
                                                                                {achievement.type === 'award' && <EmojiEvents sx={{ fontSize: 16 }} color="warning" />}
                                                                                {achievement.type === 'topic' && <CheckCircle sx={{ fontSize: 16 }} color="success" />}
                                                                                <Typography variant="body2" fontWeight="500">
                                                                                    {achievement.title}
                                                                                </Typography>
                                                                                {achievement.score && (
                                                                                    <Chip
                                                                                        label={`${achievement.score}%`}
                                                                                        size="small"
                                                                                        color="success"
                                                                                        sx={{ height: 18, fontSize: '0.7rem' }}
                                                                                    />
                                                                                )}
                                                                            </Box>
                                                                            <Typography variant="caption" color="success.dark">
                                                                                {achievement.earnedDate}
                                                                            </Typography>
                                                                            {index < course.recentAchievements.slice(0, 2).length - 1 && (
                                                                                <Divider sx={{ my: 1 }} />
                                                                            )}
                                                                        </Box>
                                                                    ))}
                                                                </Paper>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                )}
            </Box>

            {/* Quick Quiz Modal */}
            <Dialog
                open={quizModal.open}
                onClose={handleCloseQuiz}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FlashOn color="warning" />
                        <Typography variant="h6">
                            Quick Quiz {quizModal.totalQuestions > 1 && `(${quizModal.currentQuestionIndex + 1}/${quizModal.totalQuestions})`}
                        </Typography>
                    </Box>
                    <IconButton onClick={handleCloseQuiz}>
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ pt: 2 }}>
                    {quizModal.topic && quizModal.currentQuestion && !quizModal.showResult && (
                        <Box>
                            <Typography variant="subtitle1" fontWeight="500" sx={{ mb: 1 }}>
                                {quizModal.topic.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                {quizModal.topic.subject}
                            </Typography>

                            <Typography variant="body1" sx={{ mb: 3, fontWeight: 500 }}>
                                {quizModal.currentQuestion.question}
                            </Typography>

                            <FormControl component="fieldset">
                                <RadioGroup
                                    value={quizModal.selectedAnswer?.toString() || ''}
                                    onChange={(e) => handleQuizAnswerChange(e.target.value)}
                                >
                                    {quizModal.currentQuestion.options.map((option, index) => (
                                        <FormControlLabel
                                            key={index}
                                            value={index.toString()}
                                            control={<Radio />}
                                            label={option}
                                            sx={{ mb: 1 }}
                                        />
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        </Box>
                    )}

                    {quizModal.showResult && quizModal.topic && quizModal.currentQuestion && (
                        <Box sx={{ textAlign: 'center' }}>
                            {quizModal.isCorrect ? (
                                <Box>
                                    <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                                    <Typography variant="h5" color="success.main" fontWeight="bold" sx={{ mb: 1 }}>
                                        Excellent! 🎉
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 2 }}>
                                        {quizModal.currentQuestionIndex === 0 ? 'You earned 1 ReQuiz Master Point!' : 'Great job practicing!'}
                                    </Typography>
                                    <Paper sx={{ p: 2, bgcolor: 'success.50', mb: 2 }}>
                                        <Typography variant="body2" color="success.dark">
                                            <strong>Explanation:</strong> {quizModal.currentQuestion.explanation}
                                        </Typography>
                                    </Paper>
                                </Box>
                            ) : (
                                <Box>
                                    <Warning sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
                                    <Typography variant="h5" color="warning.main" fontWeight="bold" sx={{ mb: 1 }}>
                                        Not quite right
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 2 }}>
                                        The correct answer was: <strong>{quizModal.currentQuestion.options[quizModal.currentQuestion.correctAnswer]}</strong>
                                    </Typography>
                                    <Paper sx={{ p: 2, bgcolor: 'warning.50', mb: 2 }}>
                                        <Typography variant="body2" color="warning.dark">
                                            <strong>Explanation:</strong> {quizModal.currentQuestion.explanation}
                                        </Typography>
                                    </Paper>
                                    <Typography variant="body2" color="text.secondary">
                                        Keep practicing to improve!
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 3, pt: 0 }}>
                    {!quizModal.showResult ? (
                        <Button
                            variant="contained"
                            onClick={handleQuizSubmit}
                            disabled={quizModal.selectedAnswer === null}
                            startIcon={<FlashOn />}
                            fullWidth
                            size="large"
                        >
                            Submit Answer
                        </Button>
                    ) : (
                        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                            {quizModal.currentQuestionIndex < quizModal.totalQuestions - 1 ? (
                                <>
                                    <Button
                                        variant="outlined"
                                        onClick={handleCloseQuiz}
                                        size="large"
                                        sx={{ flex: 1 }}
                                    >
                                        Done
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={handleNextQuestion}
                                        size="large"
                                        sx={{ flex: 2 }}
                                        color={quizModal.isCorrect ? "success" : "primary"}
                                    >
                                        More Questions →
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    variant="contained"
                                    onClick={handleCloseQuiz}
                                    fullWidth
                                    size="large"
                                    color={quizModal.isCorrect ? "success" : "primary"}
                                >
                                    {quizModal.isCorrect ? "Great Job!" : "Well Done!"}
                                </Button>
                            )}
                        </Box>
                    )}
                </DialogActions>
            </Dialog>

        </Container>
    );
};

export default StudentCourses;
